/**
 * Life tab utility functions — real-time countdown, age, statistics.
 * All timezone-aware via browser Intl API. Performance-optimized for 1s updates.
 */

import {
  differenceInYears, differenceInDays, differenceInWeeks,
  getDayOfYear, getISODay,
} from 'date-fns'

// ──────────────────────────────────────────────
// Timezone-aware "now" (cached per second)
// ──────────────────────────────────────────────

let _cachedTz = ''
let _cachedSec = 0
let _cachedDate: Date | null = null

export function getZonedNow(timezone: string): Date {
  const sec = Math.floor(Date.now() / 1000)
  if (_cachedDate && _cachedTz === timezone && _cachedSec === sec) return _cachedDate
  const str = new Date().toLocaleString('en-US', { timeZone: timezone })
  _cachedDate = new Date(str)
  _cachedTz = timezone
  _cachedSec = sec
  return _cachedDate
}

// ──────────────────────────────────────────────
// Age calculations
// ──────────────────────────────────────────────

export function calcAge(birthDate: string): number {
  return differenceInYears(new Date(), new Date(birthDate))
}

export function calcPreciseAge(birthDate: string): number {
  const birth = new Date(birthDate)
  const now = new Date()
  return (now.getTime() - birth.getTime()) / (365.25 * 24 * 3600 * 1000)
}

export function calcDaysLived(birthDate: string): number {
  return differenceInDays(new Date(), new Date(birthDate))
}

export function calcDaysRemaining(birthDate: string, lifeExpectancy: number): number {
  const birth = new Date(birthDate)
  const end = new Date(birth.getFullYear() + lifeExpectancy, birth.getMonth(), birth.getDate())
  return Math.max(0, differenceInDays(end, new Date()))
}

export function calcLifePercentage(birthDate: string, lifeExpectancy: number): number {
  const percent = (calcPreciseAge(birthDate) / lifeExpectancy) * 100
  return Math.min(Math.round(percent * 100) / 100, 100)
}

// ──────────────────────────────────────────────
// Year countdown (to Dec 31 23:59:59 local time)
// ──────────────────────────────────────────────

export interface YearCountdown {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function calcYearCountdown(timezone: string): YearCountdown {
  const now = getZonedNow(timezone)
  const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59)
  const totalSec = Math.max(0, Math.round((endOfYear.getTime() - now.getTime()) / 1000))

  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60

  return { days, hours, minutes, seconds }
}

// ──────────────────────────────────────────────
// Life remaining countdown (to projected end date)
// Uses calendar math for years/months, then remainder for d/h/m/s
// ──────────────────────────────────────────────

export interface LifeCountdown {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function calcLifeCountdown(birthDate: string, lifeExpectancy: number): LifeCountdown {
  const birth = new Date(birthDate)
  const endDate = new Date(birth.getFullYear() + lifeExpectancy, birth.getMonth(), birth.getDate())
  const now = new Date()

  if (now >= endDate) {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  // Calendar-accurate years and months
  let years = endDate.getFullYear() - now.getFullYear()
  let months = endDate.getMonth() - now.getMonth()
  let days = endDate.getDate() - now.getDate()

  if (days < 0) {
    months -= 1
    const prevMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0)
    days += prevMonth.getDate()
  }
  if (months < 0) {
    years -= 1
    months += 12
  }

  // Remaining hours/minutes/seconds in current day
  const endOfDay = 24 * 3600
  const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  let remainingSec = endOfDay - nowSec

  // If we have remaining seconds, we need to subtract 1 day and distribute
  if (remainingSec < endOfDay) {
    // days already accounts for full days, remainingSec is partial day
    if (remainingSec === endOfDay) remainingSec = 0
  }
  if (days > 0 && remainingSec > 0) {
    days -= 1
  } else if (days === 0 && remainingSec > 0 && months > 0) {
    months -= 1
    if (months < 0) { years -= 1; months += 12 }
    const prevMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0)
    days = prevMonth.getDate() - 1
  }

  const hours = Math.floor(remainingSec / 3600)
  const minutes = Math.floor((remainingSec % 3600) / 60)
  const seconds = remainingSec % 60

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
    hours,
    minutes,
    seconds,
  }
}

// ──────────────────────────────────────────────
// Progress percentages (precise to current second)
// ──────────────────────────────────────────────

export function calcYearProgress(timezone: string): number {
  const now = getZonedNow(timezone)
  const start = new Date(now.getFullYear(), 0, 1)
  const end = new Date(now.getFullYear() + 1, 0, 1)
  const elapsed = now.getTime() - start.getTime()
  const total = end.getTime() - start.getTime()
  return Math.round((elapsed / total) * 10000) / 100
}

export function calcMonthProgress(timezone: string): number {
  const now = getZonedNow(timezone)
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  const elapsed = now.getTime() - start.getTime()
  const total = end.getTime() - start.getTime()
  return Math.round((elapsed / total) * 10000) / 100
}

export function calcWeekProgress(timezone: string): number {
  const now = getZonedNow(timezone)
  const day = getISODay(now) // 1=Mon, 7=Sun
  const secInDay = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  const totalSec = 7 * 86400
  const elapsed = (day - 1) * 86400 + secInDay
  return Math.round((elapsed / totalSec) * 10000) / 100
}

export function calcDayProgress(timezone: string): number {
  const now = getZonedNow(timezone)
  const secPassed = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  return Math.round((secPassed / 86400) * 10000) / 100
}

// ──────────────────────────────────────────────
// Timeline position helper
// ──────────────────────────────────────────────

export function ageToPercent(age: number, lifeExpectancy: number): number {
  if (lifeExpectancy <= 0) return 0
  return Math.min(Math.max((age / lifeExpectancy) * 100, 0), 100)
}

// ──────────────────────────────────────────────
// Motivational quotes
// ──────────────────────────────────────────────

const QUOTES_UZ = [
  'Har bir kun — yangi imkoniyat.',
  'Vaqtni qadrlang, u qaytib kelmaydi.',
  'Bugun qanday iz qoldirasiz?',
  'Hayot qisqa, maqsadlar katta.',
  'Har bir lahza qimmatli.',
  'Kechagi kun qaytib kelmaydi, ertangi kun kafolatlanmagan.',
  'Hozir — sen uchun eng yosh payt.',
]

const QUOTES_EN = [
  'Every day is a new opportunity.',
  'Value time — it never comes back.',
  'What mark will you leave today?',
  'Life is short, goals are big.',
  'Every moment is precious.',
  'Yesterday is gone, tomorrow is not promised.',
  'Right now — you are the youngest you will ever be.',
]

export function getMotivationalQuote(lang: string): string {
  const quotes = lang === 'uz' ? QUOTES_UZ : QUOTES_EN
  return quotes[getDayOfYear(new Date()) % quotes.length]
}

// ──────────────────────────────────────────────
// Weeks / Heartbeats / Seconds
// ──────────────────────────────────────────────

export function calcWeeksLived(birthDate: string): number {
  return differenceInWeeks(new Date(), new Date(birthDate))
}

export function calcWeeksRemaining(birthDate: string, lifeExpectancy: number): number {
  const birth = new Date(birthDate)
  const end = new Date(birth.getFullYear() + lifeExpectancy, birth.getMonth(), birth.getDate())
  return Math.max(0, differenceInWeeks(end, new Date()))
}

export function calcHeartbeats(birthDate: string): number {
  return calcDaysLived(birthDate) * 24 * 60 * 70
}

export function calcSecondsLived(birthDate: string): number {
  return Math.floor((Date.now() - new Date(birthDate).getTime()) / 1000)
}

export function formatLargeNumber(n: number, lang: string): string {
  if (n >= 1_000_000_000) {
    return (lang === 'uz' ? '~' : '~') + (n / 1_000_000_000).toFixed(1) + (lang === 'uz' ? ' mlrd' : 'B')
  }
  if (n >= 1_000_000) {
    return '~' + (n / 1_000_000).toFixed(1) + (lang === 'uz' ? ' mln' : 'M')
  }
  return n.toLocaleString()
}

